import { useCallback } from 'react';
import { useAudioManager, type SoundType } from '@/lib/audio-manager';
import type { MoveAnalysis } from '@/lib/review-engine';
import type { Piece, PieceType } from '@/lib/chess-engine';

export function useAudioFeedback() {
  const { playSound, isReady, resumeAudio } = useAudioManager();

  const playMoveSound = useCallback(() => {
    if (!isReady) return;
    playSound('move');
  }, [playSound, isReady]);

  const playCaptureSound = useCallback((capturedPiece: Piece) => {
    if (!isReady || !capturedPiece) return;
    
    const pieceType = capturedPiece.type;
    const soundMap: Record<string, SoundType> = {
      'pawn': 'pawn-capture',
      'knight': 'knight-capture', 
      'bishop': 'bishop-capture',
      'rook': 'rook-capture',
      'queen': 'queen-capture',
      'king': 'king-capture'
    };
    
    const soundType = soundMap[pieceType];
    if (soundType) {
      playSound(soundType);
    }
  }, [playSound, isReady]);

  const playMoveQualitySound = useCallback((moveLabel: MoveAnalysis['label']) => {
    if (!isReady || !moveLabel) return;
    
    const soundMap: Record<NonNullable<MoveAnalysis['label']>, SoundType> = {
      'brilliant': 'brilliant',
      'great': 'great', 
      'good': 'good',
      'inaccuracy': 'inaccuracy',
      'mistake': 'mistake',
      'blunder': 'blunder'
    };
    
    const soundType = soundMap[moveLabel];
    if (soundType) {
      playSound(soundType);
    }
  }, [playSound, isReady]);

  const playGameEventSound = useCallback((event: 'check' | 'checkmate' | 'promotion') => {
    if (!isReady) return;
    playSound(event);
  }, [playSound, isReady]);

  const playVictorySound = useCallback((opponentElo: number) => {
    if (!isReady) return;
    
    let soundType: SoundType;
    if (opponentElo < 1200) {
      soundType = 'victory-easy';
    } else if (opponentElo < 1800) {
      soundType = 'victory-medium';
    } else {
      soundType = 'victory-hard';
    }
    
    // Add slight delay for dramatic effect
    playSound(soundType, { delay: 0.3 });
  }, [playSound, isReady]);

  const playEvolutionSound = useCallback(() => {
    if (!isReady) return;
    // Use promotion sound for evolution - it's magical enough
    playSound('promotion');
  }, [playSound, isReady]);

  return {
    isReady,
    playMoveSound,
    playCaptureSound,
    playMoveQualitySound,
    playGameEventSound,
    playVictorySound,
    playEvolutionSound,
    resumeAudio
  };
}